import Configs from "../config";
import { IForm } from "../types/FormTypes";
import { DefaultResponse } from "../types/UtilTypes";
import { getVisitorId } from "../utils/FingerPrint";
import { localStorageGetItem } from "../utils/LocalStorage";

const create = async (
  body: Omit<IForm, "_id" | "userUid" | "tags" | "formTagId"> & {
    tags: string[];
  }
): Promise<IForm> => {
  const userUid = localStorageGetItem("USER_UID");
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/form`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ ...body, userUid }),
  });

  const form: DefaultResponse<IForm> = await response.json();
  if (form.status === "ok") {
    return form.data as IForm;
  }

  throw new Error(form.code);
};

const update = async (
  body: Partial<
    Omit<IForm, "userUid" | "tags" | "formTagId"> & {
      tags: string[];
    }
  > & { _id: string }
): Promise<IForm> => {
  const userUid = localStorageGetItem("USER_UID");
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/form`, {
    method: "PATCH",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ ...body, userUid }),
  });

  const form: DefaultResponse<IForm> = await response.json();
  if (form.status === "ok") {
    return form.data as IForm;
  }

  throw new Error(form.code);
};

const readOne = async (formId: string): Promise<IForm> => {
  const userUid = localStorageGetItem("USER_UID");
  const response = await fetch(`${Configs.SERVER_URL}/form/${formId}`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
    },
  });

  const forms: DefaultResponse<IForm> = await response.json();
  if (forms.status === "ok") {
    return forms.data as IForm;
  }

  throw new Error(forms.code);
};

const readOneByName = async (
  appName: string,
  formName: string
): Promise<IForm> => {
  const userUid = localStorageGetItem("USER_UID");
  const response = await fetch(
    `${Configs.SERVER_URL}/form/fromName/${appName}/${formName}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
      },
    }
  );

  const forms: DefaultResponse<IForm> = await response.json();
  if (forms.status === "ok") {
    return forms.data as IForm;
  }

  throw new Error(forms.code);
};

const readAll = async (): Promise<IForm[]> => {
  const userUid = localStorageGetItem("USER_UID");
  const response = await fetch(
    `${Configs.SERVER_URL}/form/userUid/${userUid}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
      },
    }
  );

  const forms: DefaultResponse<IForm[]> = await response.json();
  if (forms.status === "ok") {
    return forms.data as IForm[];
  }

  throw new Error(forms.code);
};

const remove = async (formId: string): Promise<IForm> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/form/${formId}`, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
  });

  const form: DefaultResponse<IForm> = await response.json();
  if (form.status === "ok") {
    return form.data as IForm;
  }

  throw new Error(form.code);
};

interface FormSubmit {
  firstName?: string;
  lastName?: string;
  formId: string;
  email: string;
  note?: string;
  password?: string;
  isFormBuilder?: boolean;
  customFields?: { [key: string]: any };
  tagIds?: string[];
}

const submit = async (data: FormSubmit) => {
  const visitorId = await getVisitorId();

  const response = await fetch(`${Configs.SERVER_URL}/contact/form`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      "X-Visitor-Id": visitorId,
    },
    body: JSON.stringify({ ...data }),
    redirect: "manual",
  });

  if (!response.ok) return null;
  const form = await response.json();

  if (form.status === "ok") {
    window.location = form.data.url;
    return true;
  }

  if (form.status === "error") {
    return {
      errorMessage: form.data.message,
    };
  }

  throw new Error(`${response.status}`);
};

interface FormCloneProps {
  name: string;
  formId: string;
}

const clone = async (body: FormCloneProps): Promise<IForm> => {
  const token = localStorageGetItem("USER_TOKEN");
  const userUid = localStorageGetItem("USER_UID");
  const response = await fetch(`${Configs.SERVER_URL}/form/clone`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({ ...body, userUid }),
  });

  const campaigns: DefaultResponse<IForm> = await response.json();
  if (campaigns.status === "ok") {
    return campaigns.data as IForm;
  }

  throw new Error(campaigns.code);
};

export const FormApi = {
  create,
  update,
  readOne,
  readAll,
  submit,
  readOneByName,
  remove,
  clone,
};
